import { faAngleDown } from "@fortawesome/pro-regular-svg-icons";
import React, { ReactElement, ReactNode, useRef } from "react";
import cx from "classnames";

import Angle from "common/components/Angle";
import Icon from "common/components/Icon";

import BaseInput, { InputProps } from "./BaseInput";

import styles from "../../styles/Select.module.less";

type OptionProps = { disabled?: boolean; value: string; children?: ReactNode };

// Pass through Option to keep the API simple here: <Select> and <Select.Option>
// to support native select dialog, particularly on mobile
const Option = (props: OptionProps) => {
  return <option {...props} />;
};

export type SelectProps = InputProps & {
  label: string;
  children: ReactNode;
  disabled?: boolean;
  /** Called when the control is blurred by clicking outside */
  onBlur?: () => void;
  placeholder?: string;
};

const isOptionChild = (child: ReactNode): child is ReactElement<OptionProps> => {
  return (
    React.isValidElement<{}>(child) &&
    "value" in child.props &&
    typeof child.props.value === "string"
  );
};

const Select = ({
  children,
  value,
  disabled,
  label,
  onBlur,
  className,
  placeholder = "Select an option",
  ...rest
}: SelectProps) => {
  const selectRef = useRef<HTMLSelectElement>(null);

  const selectedOption = React.Children.toArray(children)
    .flatMap((child) =>
      isOptionChild(child) ? [{ value: child.props.value, display: child.props.children }] : [],
    )
    .find((child) => child.value === value);

  return (
    <BaseInput
      {...rest}
      styles={{ container: className }}
      value={value}
      disabled={disabled}
      renderInputControl={({ onChange, ...controlProps }) => (
        <select
          {...controlProps}
          ref={selectRef}
          className={styles.select}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
        >
          {!value && (
            <option value="" disabled={true}>
              {placeholder}
            </option>
          )}
          {children}
        </select>
      )}
      renderInputDisplay={({ preIcon, className: cName, status }) => (
        <>
          {preIcon}
          <BaseInput.AnimationContainer label={label} className={styles.container}>
            <div className={`${styles.display} ${cName}`}>{selectedOption?.display}</div>
          </BaseInput.AnimationContainer>
          {status}
          <Angle direction="down" className={cx(styles.arrow, disabled && styles.disabled)} />
        </>
      )}
    />
  );
};

type SimpleSelectProps = React.DetailedHTMLProps<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  HTMLSelectElement
>;

const SimpleSelect = ({ className, ...other }: SimpleSelectProps) => {
  return (
    <div className={cx(styles.simpleSelect, className)}>
      <select {...other} className={styles.input} />
      <Icon icon={faAngleDown} className={styles.dropdown} />
    </div>
  );
};

export default Object.assign(Select, { Option, Simple: SimpleSelect });
